/**
 * UnitWheel.jsx
 *
 * CANONICAL viz component - Single Source of Truth
 * Location: src/ui/src/components/viz/UnitWheel.jsx
 *
 * Budget/units visualization - shows % capital deployed.
 * Pure visualization component - no edge or outcome calculations.
 *
 * Props:
 * @param {Array} roster - Array of roster slots with { units, player }
 * @param {number} totalCapacity - Total available units (default: 1000)
 * @param {boolean} compact - Use compact display mode
 */
import React from 'react';

const BRAND_COLOR = '#4338CA';

const UnitWheel = ({ roster = [], totalCapacity = 1000, compact = false }) => {
  // Calculate from roster
  const usedUnits = roster.reduce((s, r) => s + (r.units || 0), 0);
  const filledSlots = roster.filter(s => s.player).length;
  const pctDeployed = totalCapacity > 0 ? (usedUnits / totalCapacity) * 100 : 0;

  // SVG parameters
  const size = compact ? 70 : 100;
  const stroke = compact ? 7 : 10;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pctDeployed / 100) * circ;

  return (
    <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'gap-6'}`}>
      {/* Capital Deployed Circle */}
      <div className="text-center">
        <div className="relative">
          <svg width={size} height={size} className="-rotate-90">
            <circle
              cx={size/2}
              cy={size/2}
              r={radius}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth={stroke}
            />
            <circle
              cx={size/2}
              cy={size/2}
              r={radius}
              fill="none"
              stroke={BRAND_COLOR}
              strokeWidth={stroke}
              strokeDasharray={circ}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`${compact ? 'text-base' : 'text-xl'} font-black text-slate-800 tabular-nums`}>
              {pctDeployed.toFixed(0)}%
            </span>
          </div>
        </div>
        {!compact && (
          <p className="text-[10px] text-slate-400 uppercase mt-1">Capital in Play</p>
        )}
      </div>

      {!compact && (
        <>
          {/* Units in Play */}
          <div className="text-center">
            <p className="text-lg font-bold text-slate-800">${usedUnits}</p>
            <p className="text-[10px] text-slate-400 uppercase">Dollars</p>
          </div>

          {/* Slots Filled */}
          <div className="text-center">
            <p className="text-lg font-bold text-slate-800">
              {filledSlots}<span className="text-slate-400">/7</span>
            </p>
            <p className="text-[10px] text-slate-400 uppercase">Slots</p>
          </div>
        </>
      )}
    </div>
  );
};

export default UnitWheel;